import axios from 'axios';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

const ACCESS_KEY = 'fb.auth.access';
const REFRESH_KEY = 'fb.auth.refresh';

// Bare instance WITHOUT the app interceptors: a 401 from /auth/* must surface
// directly (bad credentials / dead refresh) and never re-enter the refresh loop.
// Keeping it here also avoids a circular import with api/client.ts.
const authHttp = axios.create({
  baseURL: API_BASE,
  headers: { Accept: 'application/json' },
  timeout: 15000,
});

export interface AuthUser {
  email: string;
}

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user_id: string;
  issued_at: string;
}

interface JwtPayload {
  exp?: number;
  email?: string;
  sub?: string;
  roles?: string[];
}

function decodeJwt(token: string): JwtPayload | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

function persist(t: TokenResponse): void {
  sessionStorage.setItem(ACCESS_KEY, t.access_token);
  sessionStorage.setItem(REFRESH_KEY, t.refresh_token);
}

export function clearSession(): void {
  sessionStorage.removeItem(ACCESS_KEY);
  sessionStorage.removeItem(REFRESH_KEY);
}

export function getAccessToken(): string | null {
  return sessionStorage.getItem(ACCESS_KEY);
}

export function getRefreshToken(): string | null {
  return sessionStorage.getItem(REFRESH_KEY);
}

export function isAuthenticated(): boolean {
  const token = getAccessToken();
  if (!token) return false;
  const claims = decodeJwt(token);
  if (!claims?.exp) return false;
  return claims.exp * 1000 > Date.now() + 5000; // 5s clock-skew guard
}

export function currentUser(): AuthUser | null {
  const token = getAccessToken();
  if (!token) return null;
  const claims = decodeJwt(token);
  if (!claims?.email) return null;
  return { email: claims.email };
}

export async function login(email: string, password: string): Promise<void> {
  const { data } = await authHttp.post<TokenResponse>('/auth/login', { email, password });
  persist(data);
}

let refreshInFlight: Promise<boolean> | null = null;

export async function refresh(): Promise<boolean> {
  // Dedupe concurrent 401s: parallel callers share one /auth/refresh round-trip
  // so a rotating refresh token isn't spent twice (which would clear a session
  // that was just successfully refreshed).
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async (): Promise<boolean> => {
    const rt = getRefreshToken();
    if (!rt) return false;
    try {
      const { data } = await authHttp.post<TokenResponse>('/auth/refresh', { refresh_token: rt });
      persist(data);
      return true;
    } catch {
      clearSession();
      return false;
    }
  })();
  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

export async function logout(): Promise<void> {
  const rt = getRefreshToken();
  clearSession();
  if (rt) {
    try {
      await authHttp.post('/auth/logout', { refresh_token: rt });
    } catch {
      // best-effort; local session is already cleared
    }
  }
}
